import React, { useMemo } from 'react';
import { Text } from '@react-three/drei';
import { ConfiguracionAlmacen3D } from './tipos';

interface EscenarioAlmacenProps {
  configuracion: ConfiguracionAlmacen3D;
  ancho?: number;
  largo?: number;
}

export const EscenarioAlmacen: React.FC<EscenarioAlmacenProps> = ({
  configuracion,
  ancho = 60,
  largo = 60,
}) => {
  // Paleta de piso según configuración WMS
  const pisoColor = useMemo(() => {
    switch (configuracion.tema_piso) {
      case 'epoxico_azul':
        return '#172554';
      case 'epoxico_verde':
        return '#064e3b';
      case 'madera':
        return '#78350f';
      default:
        return '#0f172a'; // Slate industrial profundo / Concreto pulido de alta tecnología
    }
  }, [configuracion.tema_piso]);

  const paredColor = useMemo(() => {
    switch (configuracion.tema_pared) {
      case 'blanco':
        return '#e2e8f0';
      case 'metalico':
        return '#475569';
      default:
        return '#1e293b';
    }
  }, [configuracion.tema_pared]);

  const altoPared = 10; // 10 metros de altura libre de nave industrial

  // Coordenadas para columnas estructurales
  const columnasX = [-ancho / 3, ancho / 3];
  const columnasZ = [-largo / 3, 0, largo / 3];

  return (
    <group>
      {/* 1. SISTEMA DE ILUMINACIÓN INDUSTRIAL */}
      <ambientLight intensity={0.55} />
      <directionalLight
        position={[20, 32, 25]}
        intensity={1.15}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-far={140}
        shadow-camera-left={-ancho / 1.4}
        shadow-camera-right={ancho / 1.4}
        shadow-camera-top={largo / 1.4}
        shadow-camera-bottom={-largo / 1.4}
      />
      <directionalLight position={[-25, 28, -20]} intensity={0.45} color="#93c5fd" />
      <hemisphereLight args={['#ffffff', '#0f172a', 0.4]} />

      {/* 2. PISO DE NAVE INDUSTRIAL */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, -0.01, 0]}
        receiveShadow
      >
        <planeGeometry args={[ancho, largo]} />
        <meshStandardMaterial
          color={pisoColor}
          roughness={configuracion.tema_piso?.startsWith('epoxico') ? 0.25 : 0.55}
          metalness={configuracion.tema_piso?.startsWith('epoxico') ? 0.3 : 0.08}
        />
      </mesh>

      {/* Cuadrícula técnica de modulación logística en el suelo */}
      <gridHelper
        args={[Math.max(ancho, largo), Math.max(ancho, largo) / 2, '#334155', '#1e293b']}
        position={[0, 0.005, 0]}
      />

      {/* 3. LÍNEAS VIALES Y DE SEGURIDAD (Tráfico pesado y peatones) */}
      {/* Carril principal de montacargas (eje central) */}
      <mesh position={[0, 0.012, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[0.2, largo - 6]} />
        <meshBasicMaterial color="#eab308" />
      </mesh>

      {/* Perímetro de seguridad amarillo */}
      {[-largo / 2 + 1.5, largo / 2 - 1.5].map((z, zi) => (
        <mesh key={`p-line-z-${zi}`} position={[0, 0.012, z]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[ancho - 3, 0.18]} />
          <meshBasicMaterial color="#eab308" />
        </mesh>
      ))}
      {[-ancho / 2 + 1.5, ancho / 2 - 1.5].map((x, xi) => (
        <mesh key={`p-line-x-${xi}`} position={[x, 0.012, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[0.18, largo - 3]} />
          <meshBasicMaterial color="#eab308" />
        </mesh>
      ))}

      {/* Paso Peatonal (Cebra de seguridad industrial cerca del muelle) */}
      <group position={[0, 0.015, largo / 2 - 6]}>
        {Array.from({ length: 9 }).map((_, i) => (
          <mesh key={`zebra-${i}`} position={[(i - 4) * 0.9, 0, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[0.55, 1.8]} />
            <meshBasicMaterial color="#ffffff" opacity={0.85} transparent />
          </mesh>
        ))}
      </group>

      {/* 4. ZONA DE STAGING / RECEPCIÓN Y DESPACHO (Líneas diagonales amarillas/negras) */}
      <group position={[-ancho / 3.2, 0.015, largo / 2 - 6]}>
        <mesh rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[8, 4]} />
          <meshStandardMaterial color="#0284c7" opacity={0.15} transparent />
        </mesh>
        {/* Bordes del área de Staging */}
        <mesh position={[0, 0, -2]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[8, 0.15]} />
          <meshBasicMaterial color="#38bdf8" />
        </mesh>
        <mesh position={[0, 0, 2]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[8, 0.15]} />
          <meshBasicMaterial color="#38bdf8" />
        </mesh>
        <mesh position={[-4, 0, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[0.15, 4]} />
          <meshBasicMaterial color="#38bdf8" />
        </mesh>
        <mesh position={[4, 0, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[0.15, 4]} />
          <meshBasicMaterial color="#38bdf8" />
        </mesh>
        <Text
          position={[0, 0.01, 0]}
          rotation={[-Math.PI / 2, 0, 0]}
          fontSize={0.4}
          color="#38bdf8"
          anchorX="center"
          anchorY="middle"
        >
          ÁREA STAGING / RECEPCIÓN
        </Text>
      </group>

      {/* 5. MUROS PERIMETRALES */}
      {/* Muro Norte (Fondo) */}
      <mesh position={[0, altoPared / 2, -largo / 2]} receiveShadow>
        <boxGeometry args={[ancho, altoPared, 0.5]} />
        <meshStandardMaterial color={paredColor} roughness={0.8} />
      </mesh>
      {/* Zócalo de seguridad amarillo/negro Muro Norte */}
      <mesh position={[0, 0.35, -largo / 2 + 0.26]}>
        <planeGeometry args={[ancho, 0.7]} />
        <meshBasicMaterial color="#facc15" />
      </mesh>

      {/* Muro Oeste (Lateral izquierdo) */}
      <mesh position={[-ancho / 2, altoPared / 2, 0]} receiveShadow>
        <boxGeometry args={[0.5, altoPared, largo]} />
        <meshStandardMaterial color={paredColor} roughness={0.8} />
      </mesh>
      <mesh position={[-ancho / 2 + 0.26, 0.35, 0]} rotation={[0, Math.PI / 2, 0]}>
        <planeGeometry args={[largo, 0.7]} />
        <meshBasicMaterial color="#facc15" />
      </mesh>

      {/* Muro Este (Lateral derecho) */}
      <mesh position={[ancho / 2, altoPared / 2, 0]} receiveShadow>
        <boxGeometry args={[0.5, altoPared, largo]} />
        <meshStandardMaterial color={paredColor} roughness={0.8} />
      </mesh>
      <mesh position={[ancho / 2 - 0.26, 0.35, 0]} rotation={[0, -Math.PI / 2, 0]}>
        <planeGeometry args={[largo, 0.7]} />
        <meshBasicMaterial color="#facc15" />
      </mesh>

      {/* Muro Sur (Frontal) - Altura reducida para permitir vista cenital sin oclusión */}
      <mesh position={[0, 0.7, largo / 2]} receiveShadow>
        <boxGeometry args={[ancho, 1.4, 0.5]} />
        <meshStandardMaterial color={paredColor} roughness={0.8} />
      </mesh>

      {/* 6. MUELLES DE CARGA Y DESCARGA (Loading Docks en Muro Norte) */}
      {[-12, 0, 12].map((dockX, di) => (
        <group key={`dock-${di}`} position={[dockX, 2.8, -largo / 2 + 0.28]}>
          {/* Portón seccional industrial */}
          <mesh>
            <boxGeometry args={[4.2, 5.2, 0.1]} />
            <meshStandardMaterial color="#334155" metalness={0.7} roughness={0.3} />
          </mesh>
          {/* Líneas horizontales del portón seccional */}
          {Array.from({ length: 6 }).map((_, li) => (
            <mesh key={`dock-line-${li}`} position={[0, (li - 2.5) * 0.8, 0.06]}>
              <boxGeometry args={[4.0, 0.04, 0.02]} />
              <meshBasicMaterial color="#1e293b" />
            </mesh>
          ))}
          {/* Marco del muelle */}
          <mesh position={[0, 2.65, 0.06]}>
            <boxGeometry args={[4.4, 0.2, 0.12]} />
            <meshStandardMaterial color="#facc15" />
          </mesh>
          <mesh position={[-2.15, 0, 0.06]}>
            <boxGeometry args={[0.15, 5.3, 0.12]} />
            <meshStandardMaterial color="#facc15" />
          </mesh>
          <mesh position={[2.15, 0, 0.06]}>
            <boxGeometry args={[0.15, 5.3, 0.12]} />
            <meshStandardMaterial color="#facc15" />
          </mesh>
          {/* Indicador LED de disponibilidad (Verde / Ocupado) */}
          <mesh position={[2.4, 2.4, 0.1]}>
            <circleGeometry args={[0.12, 16]} />
            <meshBasicMaterial color={di === 1 ? '#ef4444' : '#22c55e'} />
          </mesh>
          {/* Letrero del Andén */}
          <Text
            position={[0, 3.1, 0.1]}
            fontSize={0.35}
            color="#f8fafc"
            anchorX="center"
            anchorY="middle"
            fontWeight="bold"
          >
            {`ANDÉN 0${di + 1}`}
          </Text>
        </group>
      ))}

      {/* 7. PILARES ESTRUCTURALES DE LA NAVE CON PROTECTORES */}
      {columnasX.map((cx, cxi) =>
        columnasZ.map((cz, czi) => (
          <group key={`col-${cxi}-${czi}`} position={[cx, altoPared / 2, cz]}>
            {/* Columna I-Beam / Hormigón */}
            <mesh castShadow receiveShadow>
              <boxGeometry args={[0.6, altoPared, 0.6]} />
              <meshStandardMaterial color="#475569" roughness={0.7} metalness={0.2} />
            </mesh>
            {/* Protector de impacto amarillo en la base */}
            <mesh position={[0, -altoPared / 2 + 0.6, 0]}>
              <cylinderGeometry args={[0.55, 0.6, 1.2, 16]} />
              <meshStandardMaterial color="#facc15" roughness={0.4} metalness={0.1} />
            </mesh>
            {/* Franja negra de advertencia en el protector */}
            <mesh position={[0, -altoPared / 2 + 0.6, 0]}>
              <cylinderGeometry args={[0.56, 0.56, 0.3, 16]} />
              <meshBasicMaterial color="#0f172a" />
            </mesh>
          </group>
        ))
      )}

      {/* 8. CERCHAS METÁLICAS DEL TECHO (Ceiling Trusses) */}
      {[-largo / 3, 0, largo / 3].map((tz, ti) => (
        <group key={`truss-${ti}`} position={[0, altoPared - 0.5, tz]}>
          <mesh>
            <boxGeometry args={[ancho, 0.25, 0.25]} />
            <meshStandardMaterial color="#334155" metalness={0.8} roughness={0.3} />
          </mesh>
          {/* Luminaria Industrial High-Bay suspendida */}
          {[-ancho / 4, 0, ancho / 4].map((lx, li) => (
            <group key={`lamp-${li}`} position={[lx, -0.4, 0]}>
              <mesh>
                <cylinderGeometry args={[0.45, 0.65, 0.25, 16]} />
                <meshStandardMaterial color="#1e293b" metalness={0.7} />
              </mesh>
              <mesh position={[0, -0.13, 0]}>
                <circleGeometry args={[0.42, 16]} />
                <meshBasicMaterial color="#f8fafc" />
              </mesh>
              <pointLight position={[0, -0.4, 0]} intensity={0.4} distance={18} color="#e0f2fe" />
            </group>
          ))}
        </group>
      ))}

      {/* 9. MONTACARGAS INDUSTRIAL 3D (Forklift en reposo cerca del área de staging) */}
      <group position={[ancho / 3.4, 0, largo / 2 - 8]} rotation={[0, -Math.PI / 4, 0]}>
        {/* Chasis principal (Amarillo Industrial) */}
        <mesh position={[0, 0.6, 0]} castShadow>
          <boxGeometry args={[1.4, 0.7, 2.2]} />
          <meshStandardMaterial color="#eab308" roughness={0.3} metalness={0.2} />
        </mesh>
        {/* Contrapeso trasero (Negro pesado) */}
        <mesh position={[0, 0.6, 1.0]} castShadow>
          <boxGeometry args={[1.38, 0.68, 0.5]} />
          <meshStandardMaterial color="#0f172a" roughness={0.6} />
        </mesh>
        {/* Cabina / Tejadillo de protección (Jaula antivuelco) */}
        <mesh position={[0, 1.6, 0.1]}>
          <boxGeometry args={[1.1, 1.4, 1.2]} />
          <meshStandardMaterial color="#0f172a" wireframe />
        </mesh>
        {/* Techo de la cabina */}
        <mesh position={[0, 2.3, 0.1]}>
          <boxGeometry args={[1.12, 0.05, 1.22]} />
          <meshStandardMaterial color="#eab308" />
        </mesh>
        {/* Baliza luminosa ámbar */}
        <mesh position={[0, 2.4, 0.1]}>
          <cylinderGeometry args={[0.08, 0.08, 0.14, 12]} />
          <meshBasicMaterial color="#f97316" />
        </mesh>
        {/* Mástil de elevación frontal */}
        <mesh position={[0, 1.4, -1.15]} castShadow>
          <boxGeometry args={[0.7, 2.4, 0.12]} />
          <meshStandardMaterial color="#1e293b" metalness={0.7} />
        </mesh>
        {/* Horquillas (Uñas de acero) */}
        <mesh position={[-0.22, 0.08, -1.75]} castShadow>
          <boxGeometry args={[0.1, 0.04, 1.1]} />
          <meshStandardMaterial color="#0f172a" metalness={0.9} />
        </mesh>
        <mesh position={[0.22, 0.08, -1.75]} castShadow>
          <boxGeometry args={[0.1, 0.04, 1.1]} />
          <meshStandardMaterial color="#0f172a" metalness={0.9} />
        </mesh>
        {/* Ruedas industriales */}
        {[-0.68, 0.68].map((wx, wxi) =>
          [-0.7, 0.7].map((wz, wzi) => (
            <mesh key={`wheel-${wxi}-${wzi}`} position={[wx, 0.28, wz]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.28, 0.28, 0.2, 16]} />
              <meshStandardMaterial color="#111827" roughness={0.9} />
            </mesh>
          ))
        )}
      </group>
    </group>
  );
};
